import { useMutation, useQuery } from "@tanstack/react-query";

import { GraphQLAPI, GraphQLResult, queries } from "@examspark/api";
import { collectAllPages, getTagFromResource } from "@examspark/common";
import {
  Annotation,
  CreateAnnotationInput,
  IDSK,
  QuerySearchArgs,
  SearchableResourceConnectionNew,
  UpdateAnnotationInput,
} from "@examspark/types";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";

export const useQueryListAnnotations = (params: {
  resourceId?: string;
  workspaceId?: string;
  userId?: string;
  locationKey?: string;
}) => {
  const authorizationContext = useAuthorizationContext();
  const { resourceId, workspaceId, userId, locationKey } = params;

  return useQuery({
    enabled: !!userId && !!resourceId && !!workspaceId,
    queryKey: ["annotations", resourceId, locationKey, workspaceId, userId],
    queryFn: async () => {
      const pages = await collectAllPages(async (nextToken) => {
        const result = (await GraphQLAPI.query<{
          search: SearchableResourceConnectionNew;
        }>({
          query: queries.unit.listAnnotations,
          variables: {
            filter: {
              id: { eq: resourceId },
              or: [
                {
                  sk: { wildcard: `annotation#${userId}/user#*` },
                  workspaceId: { eq: workspaceId },
                },
                {
                  sk: { wildcard: "annotation#*/workspace#*" },
                },
              ],
            },
            authorizationContext,
            nextToken,
          } as QuerySearchArgs,
        })) as GraphQLResult<{ search: SearchableResourceConnectionNew }>;

        return result.data?.search;
      });

      return (
        pages
          .flatMap((x) => (x.items as Annotation[]) ?? [])
          .filter(
            (x) =>
              (getTagFromResource(x, "annotation-location-key")?.value ??
                undefined) === locationKey,
          ) ?? []
      );
    },
  });
};

export const useMutationCreateAnnotation = () => {
  const authorizationContext = useAuthorizationContext();

  return useMutation({
    mutationFn: async (props: { input: CreateAnnotationInput }) => {
      const result = (await GraphQLAPI.query({
        query: queries.unit.createAnnotation,
        variables: {
          input: props.input,
          authorizationContext,
        },
      })) as any;

      return result.data.createAnnotation as Annotation;
    },
  });
};

export const useMutationUpdateAnnotation = () => {
  const authorizationContext = useAuthorizationContext();

  return useMutation({
    mutationFn: async (props: { input: UpdateAnnotationInput }) => {
      const result = (await GraphQLAPI.query({
        query: queries.unit.updateAnnotation,
        variables: {
          input: props.input,
          authorizationContext,
        },
      })) as any;

      return result.data.updateAnnotation as Annotation;
    },
  });
};

export const useMutationDeleteAnnotation = () => {
  const authorizationContext = useAuthorizationContext();

  return useMutation({
    mutationFn: async (props: { input: IDSK }) => {
      const result = (await GraphQLAPI.query({
        query: queries.unit.deleteAnnotation,
        variables: {
          input: props.input,
          authorizationContext,
        },
      })) as any;

      return result.data.deleteAnnotation as Annotation;
    },
  });
};
